/**
 * The Wheel Run on the 41 slots without a road of their own (flag
 * `wheel_run`): each slot's four lamp games, the word its games are named
 * with, and its world. Plain data, no Pixi, so the app, the engine and tests
 * share it. The server decides what each lamp offers (`RoadLamp.game`) and
 * when it lights; this names the games and says which world's art a slot
 * uses. Pinned to the server's table (fixtures `catalog.wheelRun`), which is
 * plan-wheel-run-everywhere.md section 2, approved on 20 October 2026.
 */
import type { FriendGame, RingCellKind, RoadLamp, RoadState } from './road';
import { gameFriend } from './friends';

/** One Wheel Run slot. */
export interface WheelRunSlot {
  theme: string;
  /** Its node on the world map. */
  node: number;
  /** As players see it ("Kraken’s Lair"). */
  name: string;
  /** The road slot that names its world: its ring, hub, life token and
   *  game pieces come from there (the kingdom's slots: Royal Reels). */
  world: string;
  /** The short word its games are named with ("Kraken" for Kraken Cups). */
  word: string;
  /** Lamps 1 to 4: lamp 1 lit from the start, then one per star. Lamp 3 is
   *  the gold game. */
  games: readonly [FriendGame, FriendGame, FriendGame, FriendGame];
}

const SLOTS: WheelRunSlot[] = [];

function slot(theme: string, node: number, name: string, world: string, word: string, games: WheelRunSlot['games']): void {
  SLOTS.push({ theme, node, name, world, word, games });
}

// Greendale Kingdom: Forest and Royal Reels are its roads.
slot('gardens', 3, 'Castle Gardens', 'royal', 'Gardens', ['scratch', 'cups', 'wheel', 'smash']);
slot('knights', 4, 'Knights’ Quest', 'royal', 'Knights', ['cups', 'whack', 'match', 'hilo']);
slot('wizard', 5, 'Wizard’s Tower', 'royal', 'Wizard', ['pick', 'scratch', 'wheel', 'whack']);
slot('dungeons', 6, 'Castle Dungeons', 'royal', 'Dungeon', ['cups', 'smash', 'wheel', 'hilo']);
slot('dragon', 7, 'Dragon’s Hoard', 'royal', 'Dragon', ['scratch', 'dig', 'match', 'hilo']);
// Saltspray Cove. Pirate Plunder is `stormy_seas`; the Stormy Seas slot is
// the `underwater` road.
slot('map_fragment', 9, 'Map Fragment', 'underwater', 'Map', ['pick', 'scratch', 'wheel', 'hilo']);
slot('mermaid_cove', 10, 'Mermaid Cove', 'underwater', 'Mermaid', ['scratch', 'cups', 'match', 'smash']);
slot('stormy_seas', 11, 'Pirate Plunder', 'underwater', 'Pirate', ['cups', 'dig', 'wheel', 'whack']);
slot('davy_jones', 12, 'Davy Jones', 'underwater', 'Davy Jones', ['pick', 'smash', 'match', 'hilo']);
slot('kraken', 13, 'Kraken’s Lair', 'underwater', 'Kraken', ['scratch', 'whack', 'wheel', 'cups']);
slot('buried_treasure', 14, 'Buried Treasure', 'underwater', 'Treasure', ['smash', 'dig', 'match', 'hilo']);
// Frostpeak Range.
slot('avalanche', 16, 'Avalanche', 'frost', 'Avalanche', ['cups', 'dig', 'wheel', 'smash']);
slot('ice_cavern', 17, 'Ice Cavern', 'frost', 'Ice Cavern', ['scratch', 'dig', 'match', 'cups']);
slot('aurora', 18, 'Aurora', 'frost', 'Aurora', ['pick', 'hilo', 'wheel', 'scratch']);
slot('yeti', 19, 'Yeti’s Den', 'frost', 'Yeti', ['cups', 'whack', 'match', 'smash']);
slot('hot_springs', 20, 'Hot Springs', 'frost', 'Hot Springs', ['scratch', 'cups', 'wheel', 'hilo']);
slot('glacier_keep', 21, 'Glacier Keep', 'frost', 'Glacier', ['pick', 'smash', 'match', 'whack']);
// Dustwood Frontier.
slot('gold_mine', 23, 'Gold Mine', 'frontier', 'Mineshaft', ['scratch', 'dig', 'match', 'hilo']);
slot('saloon', 24, 'Saloon', 'frontier', 'Saloon', ['cups', 'hilo', 'wheel', 'smash']);
slot('bounty', 25, 'Bounty', 'frontier', 'Bounty', ['pick', 'whack', 'match', 'scratch']);
slot('iron_horse', 26, 'Iron Horse', 'frontier', 'Iron Horse', ['cups', 'smash', 'wheel', 'pick']);
slot('stampede', 27, 'Stampede', 'frontier', 'Stampede', ['scratch', 'whack', 'match', 'hilo']);
slot('high_noon', 28, 'High Noon', 'frontier', 'High Noon', ['pick', 'hilo', 'wheel', 'smash']);
// Neon Heights: nothing to dig on a city street.
slot('arcade', 30, 'Arcade', 'neon', 'Arcade', ['scratch', 'whack', 'match', 'smash']);
slot('synthwave', 31, 'Synthwave', 'neon', 'Synthwave', ['cups', 'hilo', 'wheel', 'scratch']);
slot('cyber_heist', 32, 'Cyber Heist', 'neon', 'Heist', ['pick', 'smash', 'match', 'hilo']);
slot('speedway', 33, 'Speedway', 'neon', 'Speedway', ['cups', 'whack', 'wheel', 'pick']);
slot('skyline', 34, 'Skyline', 'neon', 'Skyline', ['scratch', 'pick', 'match', 'hilo']);
slot('glitch', 35, 'Glitch', 'neon', 'Glitch', ['cups', 'whack', 'wheel', 'smash']);
// Sunscar Sands.
slot('pharaoh_tomb', 37, 'Pharaoh’s Tomb', 'caravan', 'Tomb', ['scratch', 'dig', 'match', 'smash']);
slot('pharaohs_riches', 38, 'Pharaoh’s Riches', 'caravan', 'Riches', ['pick', 'scratch', 'wheel', 'hilo']);
slot('cleopatra', 39, 'Cleopatra’s Palace', 'caravan', 'Cleopatra', ['cups', 'hilo', 'match', 'smash']);
slot('book_of_dead', 40, 'Book of the Dead', 'caravan', 'Scroll', ['scratch', 'whack', 'wheel', 'cups']);
slot('anubis', 41, 'Anubis Underworld', 'caravan', 'Anubis', ['pick', 'smash', 'match', 'whack']);
slot('eye_of_ra', 42, 'Eye of Ra', 'caravan', 'Eye of Ra', ['cups', 'dig', 'wheel', 'hilo']);
// Wonderfizz Works.
slot('lollipop_lane', 44, 'Lollipop Lane', 'sherbet_square', 'Lollipop', ['pick', 'cups', 'wheel', 'whack']);
slot('cocoa_river', 45, 'Cocoa River', 'sherbet_square', 'Cocoa', ['cups', 'dig', 'match', 'hilo']);
slot('gummy_lab', 46, 'Gummy Lab', 'sherbet_square', 'Gummy', ['scratch', 'whack', 'wheel', 'smash']);
slot('fizz_factory', 47, 'Fizz Factory', 'sherbet_square', 'Fizz', ['pick', 'hilo', 'match', 'smash']);
slot('peppermint_pipeworks', 48, 'Peppermint Pipeworks', 'sherbet_square', 'Peppermint', ['cups', 'scratch', 'wheel', 'whack']);
slot('sugarplum_vault', 49, 'Sugarplum Vault', 'sherbet_square', 'Vault', ['scratch', 'dig', 'match', 'hilo']);

/** Every Wheel Run slot by theme, in map order. */
export const WHEEL_RUN_SLOTS: Readonly<Record<string, WheelRunSlot>> = Object.fromEntries(SLOTS.map((s) => [s.theme, s]));

/** Wheel Run slots this build can draw: it has their games' names and their
 *  world's art. A slot shows its Wheel Run only where the server's list for
 *  the player (`Player.featureThemes.wheel_run`) has it too. In map order. */
export const WHEEL_RUN_THEMES: readonly string[] = SLOTS.map((s) => s.theme);

/** The lamps the slot's stars light, in order: lamp 1 from the start. */
export const LAMP_IDS = ['lamp1', 'lamp2', 'lamp3', 'lamp4'] as const satisfies readonly RingCellKind[];

/** The lamp with each Wheel Run slot's gold game. */
export const GOLD_LAMP: RingCellKind = 'lamp3';

/** The game a lamp offers instead to a player with Gamble switched off,
 *  when its own is a gamble: dig is bank or bust, so its lamp offers
 *  parcels. (A Hi-Lo lamp pays its base into the run without the climb.) */
export const NO_GAMBLE_GAME: Partial<Record<FriendGame, FriendGame>> = { dig: 'pick' };

/** How a game is named after its slot's word ("Kraken Cups"). */
export const GAME_NAME: Record<FriendGame, string> = {
  pick: 'Parcels',
  hilo: 'Hi-Lo',
  smash: 'Smash',
  match: 'Match',
  dig: 'Dig',
  scratch: 'Scratch',
  cups: 'Cups',
  wheel: 'Wheel',
  whack: 'Whack',
};

/** The road slot whose world a slot is in: a Wheel Run slot's world, or a
 *  road slot itself. A world's game pieces live under that slot's folder
 *  (`minigame/<kind>/<world>/`), so they count for every slot in it. */
export function worldOf(themeId: string): string {
  return WHEEL_RUN_SLOTS[themeId]?.world ?? themeId;
}

/** A Wheel Run lamp's game, as the panel, the cards and the run name it. */
export interface LampGameInfo {
  theme: string;
  lamp: RingCellKind;
  /** The game the lamp offers this player (a dig's stand-in with Gamble off). */
  game: FriendGame;
  /** Its name: the slot's word and the game ("Kraken Whack"). */
  title: string;
  /** The slot it's on ("Kraken’s Lair"). */
  slotName: string;
  /** The stars that light it: 0 for lamp 1. */
  stars: number;
}

/** A game's name on a Wheel Run slot ("Kraken Cups", "Pirate Parcels" for a
 *  dig's stand-in), or null on a slot without a Wheel Run. */
export function slotGameTitle(themeId: string, game: FriendGame | string): string | null {
  const slot = WHEEL_RUN_SLOTS[themeId];
  const name = GAME_NAME[game as FriendGame];
  return slot && name ? `${slot.word} ${name}` : null;
}

/** A Wheel Run slot's lamp as a game, or null for a prize lamp, a friend's
 *  lamp on a road, or a slot without a Wheel Run. The lamp names the game it
 *  offers this player; without one, the slot's own game for that lamp. */
export function lampGameOf(themeId: string, lamp: Pick<RoadLamp, 'id' | 'game' | 'friend'>): LampGameInfo | null {
  const slot = WHEEL_RUN_SLOTS[themeId];
  const index = (LAMP_IDS as readonly string[]).indexOf(lamp.id);
  if (!slot || index < 0 || lamp.friend) return null;
  const game = lamp.game ?? slot.games[index]!;
  return { theme: themeId, lamp: lamp.id, game, title: slotGameTitle(themeId, game)!, slotName: slot.name, stars: index };
}

/** Whether the road is a Wheel Run's (its lamps lit by the slot's stars),
 *  not a road with friends. */
export function isWheelRun(road: Pick<RoadState, 'stars'> | null | undefined): boolean {
  return typeof road?.stars === 'number';
}

/** The lamp a Wheel Run slot's gold pot waits for, or null elsewhere. */
export function goldLamp(themeId: string, road: Pick<RoadState, 'lamps'> | null): LampGameInfo | null {
  const lamp = road?.lamps.find((l) => l.id === GOLD_LAMP);
  return lamp ? lampGameOf(themeId, lamp) : null;
}

/** Wheel Run lamps dark in `before` and lit in `after`: the games the slot's
 *  stars have just lit, in lamp order. A first load (no `before`) lights
 *  nothing, and neither does a lamp whose game changed (another slot's). */
export function lampsLit(
  themeId: string,
  before: Pick<RoadState, 'lamps'> | null,
  after: Pick<RoadState, 'lamps'> | null,
): LampGameInfo[] {
  if (!before || !after) return [];
  return after.lamps
    .filter((l) => l.lit && !l.friend && before.lamps.some((b) => b.id === l.id && !b.lit && b.game === l.game))
    .map((l) => lampGameOf(themeId, l))
    .filter((g): g is LampGameInfo => g !== null);
}

/** A game's name on a road or a Wheel Run slot: the friend's whose lamp
 *  offers it there, else the slot's own lamp game, else null. */
export function lampGameTitle(themeId: string | null, road: Pick<RoadState, 'lamps'> | null, game: string): string | null {
  const friend = gameFriend(road, game);
  if (friend) return friend.title;
  return themeId ? slotGameTitle(themeId, game) : null;
}

/** Stars as words, for lines read by players: the star glyph can't be
 *  trusted to draw on every phone, so the panel draws stars as pictures. */
export function starsInWords(stars: number): string {
  return ['no stars', 'one star', 'two stars', 'three stars'][Math.max(0, Math.min(3, stars))]!;
}
